/**
 * Shopify Language-Based Image Switcher
 * 
 * This script automatically replaces images with "Eng" prefix based on the current language
 * Works with Shopify Translate & Adapt app
 * 
 * Supported languages:
 * - English (en): Eng prefix
 * - Chinese (zh-CN, zh-TW, zh): Chi prefix
 * - Malay (ms): May prefix
 * - Japanese (ja): Jap prefix
 * - Default: Eng prefix
 */

(function() {
  'use strict';
  
  // Language prefix mapping
  const languagePrefixMap = {
    'en': 'Eng',
    'zh': 'Chi',
    'zh-CN': 'Chi',
    'zh-TW': 'Chi',
    'ms': 'May',
    'ja': 'Jap'
  };
  
  /**
   * Get current language from Shopify
   * @returns {string} Language code
   */
  function getCurrentLanguage() {
    if (window.Shopify && window.Shopify.locale) {
      return window.Shopify.locale;
    }
    
    const htmlLang = document.documentElement.lang;
    if (htmlLang) {
      return htmlLang.toLowerCase();
    }
    
    return 'en';
  }
  
  /**
   * Get the appropriate prefix for current language
   * @returns {string} Language prefix (e.g., 'Eng', 'Chi')
   */
  function getLanguagePrefix() {
    const currentLang = getCurrentLanguage();
    return languagePrefixMap[currentLang] || 'Eng';
  }
  
  /**
   * Check if an image URL exists
   * @param {string} url - Image URL to check
   * @returns {Promise<boolean>} True if image exists
   */
  function checkImageExists(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }
  
  /**
   * Replace image URL with language-specific version
   * @param {string} url - Original image URL
   * @param {Function} callback - Callback with final URL
   */
  function replaceImageUrl(url, callback) {
    if (!url.includes('/Eng_') && !url.includes('/Eng-')) {
      if (callback) callback(url);
      return url;
    }
    
    const newPrefix = getLanguagePrefix();
    
    if (newPrefix === 'Eng') {
      if (callback) callback(url);
      return url;
    }
    
    const newUrl = url
      .replace(/\/Eng_/g, `/${newPrefix}_`)
      .replace(/\/Eng-/g, `/${newPrefix}-`);
    
    if (callback) {
      checkImageExists(newUrl).then(exists => {
        if (exists) {
          callback(newUrl);
        } else {
          callback(url);
        }
      });
    } else {
      return newUrl;
    }
  }
  
  /**
   * Update all images with Eng prefix
   */
  function updateImages() {
    const images = document.querySelectorAll('img[src*="/Eng"], img[data-src*="/Eng"]');
    
    images.forEach(img => {
      if (img.src && img.src.includes('/Eng')) {
        const oldSrc = img.src;
        replaceImageUrl(img.src, (newUrl) => {
          img.src = newUrl;
        });
      }
      
      if (img.dataset.src && img.dataset.src.includes('/Eng')) {
        replaceImageUrl(img.dataset.src, (newUrl) => {
          img.dataset.src = newUrl;
        });
      }
      
      if (img.srcset && img.srcset.includes('/Eng')) {
        const srcsetParts = img.srcset.split(',');
        let processedCount = 0;
        
        srcsetParts.forEach((src, index) => {
          const parts = src.trim().split(' ');
          const url = parts[0];
          
          replaceImageUrl(url, (newUrl) => {
            parts[0] = newUrl;
            srcsetParts[index] = parts.join(' ');
            processedCount++;
            
            if (processedCount === srcsetParts.length) {
              img.srcset = srcsetParts.join(', ');
            }
          });
        });
      }
    });
    
    const elementsWithBg = document.querySelectorAll('[style*="Eng"]');
    
    elementsWithBg.forEach(el => {
      const style = el.getAttribute('style');
      if (style && style.includes('/Eng')) {
        const urlMatch = style.match(/url\(['"]?([^'"]+)['"]?\)/);
        if (urlMatch && urlMatch[1]) {
          const oldUrl = urlMatch[1];
          replaceImageUrl(oldUrl, (newUrl) => {
            const newStyle = style.replace(oldUrl, newUrl);
            el.setAttribute('style', newStyle);
          });
        }
      }
    });
  }
  
  /**
   * Initialize the image switcher
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateImages);
    } else {
      updateImages();
    }
    
    if (window.MutationObserver) {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.attributeName === 'lang') {
            updateImages();
          }
        });
      });
      
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['lang']
      });
    }
    
    document.addEventListener('shopify:language:change', updateImages);
  }
  
  window.shopifyLanguageImageSwitcher = {
    updateImages: updateImages,
    replaceImageUrl: replaceImageUrl,
    getCurrentLanguage: getCurrentLanguage
  };
  
  init();
})();